import {
  Toaster as ChakraToaster,
  Portal,
  Spinner,
  Stack,
  Toast,
  createToaster,
} from "@chakra-ui/react";

/**
 * The one place a transient message is shown.
 *
 * Pages used to keep their own `flash` state and render an alert at the top,
 * which meant the confirmation for something you did at the bottom of a long
 * page appeared where you were not looking — and every page said it slightly
 * differently. A toaster is a single overlay outside the page's own scroll,
 * so the message is where the eye is regardless of what was clicked.
 *
 * Kept for things that HAPPENED: saved, assigned, resumed. A message that
 * needs to persist — a validation problem the reader has to fix before
 * continuing — belongs beside the field, not in something that fades.
 */
export const toaster = createToaster({
  placement: "bottom-end",
  pauseOnPageIdle: true,
  max: 4,
  // Long enough to finish reading after looking away. The default assumes the
  // reader was watching the corner when it appeared, and nobody is.
  duration: 10000,
});

export function Toaster() {
  return (
    <Portal>
      {/* Above the drawer and the dialogs. Both render into portals of their
          own, later in the document, so at an equal z-index they paint over
          the toast -- which is why a confirmation for something done inside
          the assign drawer was never seen. */}
      <ChakraToaster toaster={toaster} insetInline={{ mdDown: "4" }} zIndex={2000}>
        {(toast) => (
          <Toast.Root width={{ md: "sm" }}>
            {toast.type === "loading" ? (
              <Spinner size="sm" color="blue.solid" />
            ) : (
              <Toast.Indicator />
            )}
            <Stack gap="1" flex="1" maxWidth="100%">
              {toast.title ? <Toast.Title>{toast.title}</Toast.Title> : null}
              {toast.description ? (
                <Toast.Description>{toast.description}</Toast.Description>
              ) : null}
            </Stack>
            {toast.action ? (
              <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>
            ) : null}
            {toast.meta?.closable ? <Toast.CloseTrigger /> : null}
          </Toast.Root>
        )}
      </ChakraToaster>
    </Portal>
  );
}

/** Something worked. */
export function toastSuccess(title: string, description?: string) {
  toaster.create({ type: "success", title, description });
}

/**
 * Something did not.
 *
 * Longer-lived than a success and closable by hand: a failure is read, and
 * sometimes copied into a message to somebody, where a success is only
 * glanced at.
 */
export function toastError(title: string, description?: string) {
  toaster.create({
    type: "error",
    title,
    description,
    duration: 14000,
    meta: { closable: true },
  });
}
